import type { CampaignCreationCapabilities } from "@adpilot/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { fetchSponsoredBrandRecommendations } from "@/lib/campaign-creation-api";
import { keywordKey, parseKeywordList } from "./sponsored-brands-format";
import {
  type AdFormat,
  type CategoryTargetRow,
  type KeywordAddResult,
  type KeywordRow,
  MATCH_TYPES,
  type MatchType,
  type NegativeKeywordRow,
  type RecommendationType,
  type TargetingType,
} from "./sponsored-brands-model";

interface UseSponsoredBrandsTargetingOptions {
  shopId: string;
  adFormat: AdFormat;
  /** ASINs that seed keyword/product/category recommendations. */
  recommendationAsins: string[];
  bidCapabilities: CampaignCreationCapabilities["bid"];
}

/** Keyword and product targeting state, including Amazon recommendation loading. */
export function useSponsoredBrandsTargeting({
  shopId,
  adFormat,
  recommendationAsins,
  bidCapabilities,
}: UseSponsoredBrandsTargetingOptions) {
  const [targetingType, setTargetingType] = useState<TargetingType>("keyword");
  const [productTargetInput, setProductTargetInput] = useState("");
  const [productTargetAsins, setProductTargetAsins] = useState<string[]>([]);
  const [categoryTargets, setCategoryTargets] = useState<CategoryTargetRow[]>([]);
  const [keywordSuggestions, setKeywordSuggestions] = useState<KeywordRow[]>([]);
  const [categorySuggestions, setCategorySuggestions] = useState<CategoryTargetRow[]>([]);
  const [productSuggestions, setProductSuggestions] = useState<string[]>([]);
  const [negativeProductInput, setNegativeProductInput] = useState("");
  const [negativeProductAsins, setNegativeProductAsins] = useState<string[]>([]);
  const [keywordTab, setKeywordTab] = useState("suggestions");
  const [keywordInput, setKeywordInput] = useState("");
  const defaultBid = Math.min(bidCapabilities.max, Math.max(bidCapabilities.min, 0.75)).toFixed(
    bidCapabilities.decimals,
  );
  const [keywordBid, setKeywordBid] = useState(defaultBid);
  // Read through a ref so refreshed suggestions use the current bid without re-deriving on bid edits.
  const keywordBidRef = useRef(keywordBid);
  const [productTargetBid, setProductTargetBid] = useState(defaultBid);
  const [keywordRows, setKeywordRows] = useState<KeywordRow[]>([]);
  const [matchTypes, setMatchTypes] = useState<Record<MatchType, boolean>>({
    B: true,
    P: true,
    E: true,
  });
  const [negativeInput, setNegativeInput] = useState("");
  const [negativeKeywords, setNegativeKeywords] = useState<NegativeKeywordRow[]>([]);
  const [keywordAddResult, setKeywordAddResult] = useState<KeywordAddResult | null>(null);
  const recommendations = useMutation({
    mutationFn: (type: RecommendationType) =>
      fetchSponsoredBrandRecommendations(shopId, type, recommendationAsins, adFormat),
  });
  const keywordRecommendations = useQuery({
    queryKey: [
      "campaign-creation",
      "sponsored-brands",
      "keyword-recommendations",
      shopId,
      recommendationAsins,
      adFormat,
    ],
    queryFn: () =>
      fetchSponsoredBrandRecommendations(shopId, "keyword", recommendationAsins, adFormat),
    enabled:
      targetingType === "keyword" &&
      adFormat === "productCollection" &&
      recommendationAsins.length > 0,
  });
  useEffect(() => {
    keywordBidRef.current = keywordBid;
  }, [keywordBid]);
  useEffect(() => {
    if (!keywordRecommendations.data) return;
    const keywords = [
      ...new Set(
        keywordRecommendations.data.flatMap((value) =>
          value.type === "keyword" ? [value.value] : [],
        ),
      ),
    ];
    setKeywordSuggestions(
      keywords.flatMap((keyword) =>
        MATCH_TYPES.map((matchType) => ({
          id: `${matchType}:${keyword}`,
          keyword,
          matchType,
          bid: keywordBidRef.current,
          suggestedBid: null,
        })),
      ),
    );
  }, [keywordRecommendations.data]);

  const addKeywords = () => {
    const keywords = parseKeywordList(keywordInput);
    const existing = new Set(keywordRows.map((row) => keywordKey(row.matchType, row.keyword)));
    const next = [...keywordRows];
    let added = 0;
    const attempted = keywords.length * MATCH_TYPES.filter((type) => matchTypes[type]).length;
    for (const keyword of keywords) {
      for (const matchType of MATCH_TYPES) {
        const key = keywordKey(matchType, keyword);
        if (!matchTypes[matchType] || existing.has(key)) continue;
        existing.add(key);
        next.push({ id: crypto.randomUUID(), keyword, matchType, bid: keywordBid });
        added += 1;
      }
    }
    setKeywordRows(next);
    setKeywordAddResult({ added, attempted });
    setKeywordInput("");
  };

  const addKeywordSuggestions = (rows: KeywordRow[]) =>
    setKeywordRows((current) => [
      ...current,
      ...rows.filter(
        (row) =>
          !current.some(
            (existing) =>
              existing.matchType === row.matchType &&
              existing.keyword.toLowerCase() === row.keyword.toLowerCase(),
          ),
      ),
    ]);

  const loadRecommendations = async (type: RecommendationType) => {
    if (recommendationAsins.length === 0) return;
    const values = await recommendations.mutateAsync(type);
    if (type === "keyword") {
      setKeywordSuggestions(
        values.flatMap((value) =>
          value.type === "keyword"
            ? [
                {
                  id: crypto.randomUUID(),
                  keyword: value.value,
                  matchType: ({ BROAD: "B", PHRASE: "P", EXACT: "E" } as const)[value.matchType],
                  bid: keywordBid,
                },
              ]
            : [],
        ),
      );
    } else if (type === "category") {
      setCategorySuggestions(
        values.flatMap((value) =>
          value.type === "category" ? [{ id: value.id, name: value.name }] : [],
        ),
      );
    } else {
      setProductSuggestions(
        values.flatMap((value) => (value.type === "product" ? [value.asin] : [])),
      );
    }
  };

  return {
    targetingType,
    setTargetingType,
    keywordTab,
    setKeywordTab,
    keywordInput,
    setKeywordInput,
    keywordRows,
    setKeywordRows,
    matchTypes,
    setMatchTypes,
    keywordBid,
    setKeywordBid,
    addKeywords,
    keywordAddResult,
    keywordSuggestions,
    addKeywordSuggestions,
    keywordSuggestionsLoading: keywordRecommendations.isLoading,
    negativeInput,
    setNegativeInput,
    negativeKeywords,
    setNegativeKeywords,
    productTargetInput,
    setProductTargetInput,
    productTargetAsins,
    setProductTargetAsins,
    categoryTargets,
    setCategoryTargets,
    categorySuggestions,
    productSuggestions,
    loadRecommendations,
    recommendationsLoading: recommendations.isPending,
    productTargetBid,
    setProductTargetBid,
    negativeProductInput,
    setNegativeProductInput,
    negativeProductAsins,
    setNegativeProductAsins,
  };
}
